"use client";

import { useEffect, useRef, useState } from "react";

const sessionRateLimited = { value: false, until: 0 };

interface UseAiCardOptions {
  kind: string;
  location: string;
  summary: object;
  enabled?: boolean;
}

export function useAiCard({ kind, location, summary, enabled = true }: UseAiCardOptions) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"rate_limit" | "error" | null>(null);
  const lastKey = useRef<string>("");

  const summaryKey = JSON.stringify(summary);
  const fullKey = `${kind}_${location}_${summaryKey}`;

  const fetch_ = async () => {
    // Skip if rate-limited globally
    if (sessionRateLimited.value && Date.now() < sessionRateLimited.until) {
      setError("rate_limit");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, location, summary }),
      });

      if (res.status === 429 || res.status === 502) {
        const data = await res.json().catch(() => ({}));
        if (typeof data?.error === "string" && data.error.includes("429")) {
          // Set rate-limit flag for the next hour
          sessionRateLimited.value = true;
          sessionRateLimited.until = Date.now() + 60 * 60 * 1000;
          setError("rate_limit");
        } else {
          setError("error");
        }
        return;
      }

      const data = await res.json();
      if (data.text) {
        setText(data.text);
      } else {
        setError("error");
      }
    } catch {
      setError("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    if (!Object.keys(summary).length) return;
    if (lastKey.current === fullKey) return;
    lastKey.current = fullKey;
    fetch_();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullKey, enabled]);

  const refresh = () => {
    sessionRateLimited.value = false;
    lastKey.current = "";
    fetch_();
  };

  return { text, loading, error, refresh };
}
